import React, { useEffect, useState } from 'react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { Button, Input } from '@/shared/ui'
import { useAdminRoleCatalog } from '@/shared/hooks'
import type { AdminRole } from '@/shared/api'

interface Props {
  isOpen: boolean
  role: AdminRole | null
  loading?: boolean
  onClose: () => void
  onSubmit: (data: { name: string; description: string; permissions: string[] }) => void | Promise<void>
}

export const RoleFormModal: React.FC<Props> = ({ isOpen, role, loading, onClose, onSubmit }) => {
  const { data: catalog } = useAdminRoleCatalog()
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [selected, setSelected] = useState<Set<string>>(new Set())

  useEffect(() => {
    if (!isOpen) return
    setName(role?.name || '')
    setDescription(role?.description || '')
    setSelected(new Set((role?.permissions || []).map((p) => `${p.section}.${p.action}`)))
  }, [isOpen, role])

  if (!isOpen) return null

  const toggle = (key: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      next.has(key) ? next.delete(key) : next.add(key)
      return next
    })
  }

  const toggleSection = (keys: string[], on: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev)
      keys.forEach((k) => (on ? next.add(k) : next.delete(k)))
      return next
    })
  }

  const handleSubmit = () => {
    if (!name.trim()) return
    void onSubmit({
      name: name.trim(),
      description: description.trim(),
      permissions: Array.from(selected),
    })
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-40 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">
            {role ? `Роль «${role.name}»` : 'Новая роль'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>
        <div className="p-6 space-y-5">
          <Input label="Название" value={name} onChange={(e) => setName(e.target.value)}
                 placeholder="Например, Оператор заявок" />
          <Input label="Описание" value={description}
                 onChange={(e) => setDescription(e.target.value)}
                 placeholder="Кратко, за что отвечает роль" />
          <div className="space-y-4">
            <div className="text-sm font-medium text-gray-700">Права</div>
            {(catalog?.sections || []).map((section) => {
              const keys = section.actions.map((a) => `${section.key}.${a.key}`)
              const allOn = keys.every((k) => selected.has(k))
              return (
                <div key={section.key} className="border border-gray-200 rounded-lg p-3">
                  <label className="flex items-center gap-2 mb-2 font-medium text-gray-800">
                    <input type="checkbox" checked={allOn}
                           onChange={(e) => toggleSection(keys, e.target.checked)} />
                    {section.label}
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2 pl-6">
                    {section.actions.map((action) => {
                      const key = `${section.key}.${action.key}`
                      return (
                        <label key={key} className="flex items-center gap-2 text-sm text-gray-700">
                          <input type="checkbox" checked={selected.has(key)}
                                 onChange={() => toggle(key)} />
                          {action.label}
                        </label>
                      )
                    })}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
        <div className="flex justify-end gap-3 p-6 border-t bg-gray-50">
          <Button variant="secondary" onClick={onClose} disabled={loading}>Отмена</Button>
          <Button onClick={handleSubmit} isLoading={!!loading} disabled={!name.trim()}>Сохранить</Button>
        </div>
      </div>
    </div>
  )
}
